import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  Add,
  Check,
  Close,
  Delete,
  Edit,
  People,
} from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ClientRecipientJunction,
  useCreateAuthorizedRecipientMutation,
  useDeleteAuthorizedRecipientMutation,
  useUpdateAuthorizedRecipientMutation,
} from "../../apis/appApi"

const RELATIONS = [
  "spouse",
  "parent",
  "familyChild",
  "friend",
  "reception",
  "nurse",
  "other",
  "rxpro",
] as const

type AuthorizedRecipientSectionProps = {
  clientId: number
  recipients: ClientRecipientJunction[]
  onSuccess: (message: string) => void
}

type EditState = {
  id: number | null // null = new
  name: string
  relation: string
}

const relationI18nKey: Record<string, string> = {
  spouse: "clients.recipient.relation.spouse",
  parent: "clients.recipient.relation.parent",
  familyChild: "clients.recipient.relation.familyChild",
  friend: "clients.recipient.relation.friend",
  reception: "clients.recipient.relation.reception",
  nurse: "clients.recipient.relation.nurse",
  other: "clients.recipient.relation.other",
  rxpro: "clients.recipient.relation.rxpro",
}

export const AuthorizedRecipientSection: FC<AuthorizedRecipientSectionProps> = ({
  clientId,
  recipients,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [createRecipient, { isLoading: isCreating }] = useCreateAuthorizedRecipientMutation()
  const [updateRecipient, { isLoading: isUpdating }] = useUpdateAuthorizedRecipientMutation()
  const [deleteRecipient] = useDeleteAuthorizedRecipientMutation()

  const [editState, setEditState] = useState<EditState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ClientRecipientJunction | null>(null)

  const isSaving = isCreating || isUpdating

  const handleStartAdd = useCallback(() => {
    setEditState({ id: null, name: "", relation: "other" })
  }, [])

  const handleStartEdit = useCallback((item: ClientRecipientJunction) => {
    if (!item.recipient) return
    setEditState({
      id: item.recipientId,
      name: item.recipient.name,
      relation: item.recipient.relation,
    })
  }, [])

  const handleCancel = useCallback(() => {
    setEditState(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!editState) return
    const trimmedName = editState.name.trim()
    if (!trimmedName) return

    try {
      if (editState.id === null) {
        await createRecipient({
          clientId,
          name: trimmedName,
          relation: editState.relation,
        }).unwrap()
        onSuccess(t("clients.recipient.added"))
      } else {
        await updateRecipient({
          clientId,
          id: editState.id,
          name: trimmedName,
          relation: editState.relation,
        }).unwrap()
        onSuccess(t("clients.recipient.updated"))
      }
      setEditState(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [editState, clientId, createRecipient, updateRecipient, onSuccess, t])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteRecipient({ clientId, id: deleteTarget.recipientId }).unwrap()
      onSuccess(t("clients.recipient.deleted"))
      setDeleteTarget(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [deleteTarget, clientId, deleteRecipient, onSuccess, t])

  const isNameValid = editState ? editState.name.trim().length > 0 : false

  return (
    <Paper {...uiSurface("UI-DLV2-090")} variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <People fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {t("clients.recipient.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({recipients.length})
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {recipients.map((item) => {
          const rec = item.recipient
          if (!rec) return null
          const isEditing = editState?.id === item.recipientId

          if (isEditing) {
            return (
              <Stack
                key={item.recipientId}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <TextField
                  size="small"
                  value={editState.name}
                  onChange={(e) =>
                    setEditState((s) => (s ? { ...s, name: e.target.value } : s))
                  }
                  placeholder={t("clients.recipient.name")}
                  sx={{ flex: 1 }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isNameValid) handleSave()
                    if (e.key === "Escape") handleCancel()
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={editState.relation}
                  onChange={(e) =>
                    setEditState((s) => (s ? { ...s, relation: e.target.value } : s))
                  }
                  sx={{ minWidth: 130 }}
                >
                  {RELATIONS.map((r) => (
                    <MenuItem key={r} value={r}>
                      {t(relationI18nKey[r])}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSave}
                  disabled={!isNameValid || isSaving}
                >
                  {isSaving ? <CircularProgress size={18} /> : <Check fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            )
          }

          return (
            <Stack
              key={item.recipientId}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 0.5,
                "&:hover .recipient-actions": { opacity: 1 },
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {rec.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                {t(relationI18nKey[rec.relation] || rec.relation)}
              </Typography>
              <Stack
                className="recipient-actions"
                direction="row"
                spacing={0}
                sx={{ opacity: 0, transition: "opacity 0.15s" }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleStartEdit(item)}
                  disabled={editState !== null}
                >
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(item)}
                  disabled={editState !== null}
                >
                  <Delete sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          )
        })}

        {/* Add new recipient inline form */}
        {editState?.id === null && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              value={editState.name}
              onChange={(e) =>
                setEditState((s) => (s ? { ...s, name: e.target.value } : s))
              }
              placeholder={t("clients.recipient.name")}
              sx={{ flex: 1 }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && isNameValid) handleSave()
                if (e.key === "Escape") handleCancel()
              }}
            />
            <TextField
              select
              size="small"
              value={editState.relation}
              onChange={(e) =>
                setEditState((s) => (s ? { ...s, relation: e.target.value } : s))
              }
              sx={{ minWidth: 130 }}
            >
              {RELATIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(relationI18nKey[r])}
                </MenuItem>
              ))}
            </TextField>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={!isNameValid || isSaving}
            >
              {isSaving ? <CircularProgress size={18} /> : <Check fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {recipients.length === 0 && editState === null && (
          <Typography variant="body2" color="text.disabled">
            {t("clients.recipient.noRecipients")}
          </Typography>
        )}
      </Stack>

      {editState === null && (
        <Button
          size="small"
          startIcon={<Add />}
          onClick={handleStartAdd}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {t("clients.recipient.add")}
        </Button>
      )}

      {/* Delete confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-091") }}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>{t("clients.recipient.deleteConfirm")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteTarget?.recipient?.name}
            {deleteTarget?.recipient?.relation
              ? ` (${t(relationI18nKey[deleteTarget.recipient.relation] || deleteTarget.recipient.relation)})`
              : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
